import { rojoCustomElement } from '../../lib/dependencies';
import { util, Util } from '../../lib/util';

import './styles.css';

const TOAST_CLASS_BASE = 'p-sm text-primary-text rounded fixed top-lg left-lg';
const TOAST_CLASS_ERROR = `${TOAST_CLASS_BASE} bg-error`;
const TOAST_CLASS_SUCCESS = `${TOAST_CLASS_BASE} bg-primary border-2 border-highlighter`;
const TOAST_DURATION_MS = 4000;
const GLOBAL_ERROR_TOAST_THROTTLE_MS = 3000;

/**
 * Prepend a transient message element to `host`, auto-removing it after
 * TOAST_DURATION_MS. Shared by RojoElement.flash() and the global safety net
 * below so the two stay visually identical.
 */
function renderToast(host: Element, message: string, isError: boolean) {
    const el = document.createElement('p');
    el.textContent = message;
    el.setAttribute('role', isError ? 'alert' : 'status');
    el.className = isError ? TOAST_CLASS_ERROR : TOAST_CLASS_SUCCESS;
    host.prepend(el);
    setTimeout(() => el.remove(), TOAST_DURATION_MS);
}

/***
 * Global safety net for otherwise-silent client failures.
 *
 * webcomponents/page.js loads once per page, in <head>, before every other
 * component bundle (layout.pug / hopper-layout.pug), so this is the place to
 * catch promise rejections that no component handled — e.g. a fetch that
 * rejected or a non-JSON response — which would otherwise leave a button
 * looking dead with no feedback.
 */
let lastGlobalErrorToastAt = 0;

function showGlobalErrorToast(message: string) {
    const now = Date.now();
    // Don't stack toasts when rejections arrive in a burst.
    if (now - lastGlobalErrorToastAt < GLOBAL_ERROR_TOAST_THROTTLE_MS) {
        return;
    }
    lastGlobalErrorToastAt = now;

    const host = document.querySelector('rojo-page') ?? document.body;
    if (host) {
        renderToast(host, message, true);
    }
}

window.addEventListener('unhandledrejection', (e) => {
    console.error('Unhandled promise rejection:', e.reason);
    showGlobalErrorToast('Something went wrong — please try again.');
});

window.addEventListener('error', (e) => {
    // Log only — a toast per sync script error would be noisy.
    console.error('Uncaught error:', e.error ?? e.message);
});

/***
 * RojoElement is an abstract base class akin to PhoenixElement
 * in the tempest-phoenix repository.
 */
export class RojoElement extends HTMLElement {
    getCookieValue(name: string): string {
        const cookie = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
        if (cookie) {
            // Value may contain equals sign, we only want to split on the first
            return cookie.split(/=(.+)$/)[1];
        }
        return '';
    }

    emitEvent(eventName: string, value: string, bubbles = true) {
        const event = new CustomEvent(eventName, {
            bubbles,
            detail: {
                value,
            },
        });
        window.dispatchEvent(event);
    }

    flash(message: string, isError: boolean = true) {
        renderToast(this, message, isError);
    }

    flashSuccess(message: string) {
        this.flash(message, false);
    }

    /**
     * Briefly highlight a table row to show it was just updated. Only recolors
     * text in cells that don't contain a form control — inputs/selects keep a
     * white background, so light text there would be unreadable against it.
     */
    highlightRowSuccess(row: HTMLTableRowElement, durationMs: number = 4000) {
        const textCells = Array.from(row.querySelectorAll<HTMLElement>('td')).filter(
            (td) => !td.querySelector('input, select')
        );
        row.classList.add('bg-primary');
        textCells.forEach((td) => td.classList.add('text-primary-text'));
        setTimeout(() => {
            row.classList.remove('bg-primary');
            textCells.forEach((td) => td.classList.remove('text-primary-text'));
        }, durationMs);
    }

    connectedCallback() {
        // Nothing to do yet
    }

    disconnectedCallback() {
        // Nothing to do yet
    }
}

/*********
 * RojoPage
 *
 * Following the pattern of PhoenixPage in the tempest-phoenix repository,
 * this custom element is a vehicle for shared pieces of code in the form
 * of static methods. (Maybe eventually it will include page-level configuration
 * data as the Phoenix version does.)
 */

class RojoPage extends RojoElement {
    /**
     * Expose the util module.
     */
    static get util(): Util {
        return util;
    }

    /**
     * Expose the RojoElement base class.
     */
    static get RojoElement() {
        return RojoElement;
    }
}

rojoCustomElement('rojo-page', RojoPage);
